import React, { useState, useRef, useEffect } from 'react';
import { EllipsisVerticalIcon } from '@revfluence/fresh-icons/regular/esm';
import { Input } from '@/shadcn/components/ui/input';
import { Button } from '@/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shadcn/components/ui/alert-dialog';
import { validateEdgeLabel, MAX_EDGE_LABEL_LENGTH } from './EdgeLabelManager';

interface EdgeLabelEditorProps {
  label: string;
  onSave: (newLabel: string) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onDeleteNode?: () => void;
  onDeletePath?: () => void;
  className?: string;
  isEditingMode?: boolean; // New prop to control DropdownMenu visibility
}

export const EdgeLabelEditor: React.FC<EdgeLabelEditorProps> = ({
  label,
  onSave,
  onDelete,
  onDuplicate,
  onDeleteNode,
  onDeletePath,
  className = '',
  isEditingMode = true, // Default to true to maintain existing behavior
}) => {
  const [isHovered, setIsHovered] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(label || '');
  const [error, setError] = useState<string | null>(null);
  const [showDeletePathDialog, setShowDeletePathDialog] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    // Update edit value when label changes externally
    setEditValue(label || '');
  }, [label]);

  // Close dropdown and reset hover state when component unmounts or major operations occur
  useEffect(() => {
    return () => {
      setIsDropdownOpen(false);
      setIsHovered(false);
    };
  }, []);

  const handleSave = () => {
    const validation = validateEdgeLabel(editValue);
    if (!validation.valid) {
      setError(validation.error || 'Invalid label');
      return;
    }

    const trimmedValue = editValue.trim();
    onSave(trimmedValue);
    setIsEditing(false);
    setError(null);
  };

  const handleBlur = () => {
    // Auto-save on blur (clicking outside)
    const currentLabel = label || '';
    const newValue = editValue.trim();

    // Always try to save if there's a non-empty value or if the value changed
    if (newValue && newValue !== currentLabel.trim()) {
      handleSave();
    } else if (!newValue && currentLabel) {
      // If user cleared the label, also save (set to empty)
      handleSave();
    } else {
      setIsEditing(false);
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setEditValue(label);
      setIsEditing(false);
      setError(null);
    }
  };

  const handleRename = () => {
    setIsEditing(true);
    setIsDropdownOpen(false); // Close dropdown when entering edit mode
  };

  const handleDeletePath = () => {
    // Close dropdown and show confirmation dialog
    setIsDropdownOpen(false);
    setShowDeletePathDialog(true);
  };

  const confirmDeletePath = () => {
    if (onDeletePath) {
      // Reset state and delete the entire path
      setIsHovered(false);
      onDeletePath();
    } else if (onDelete) {
      // Fallback: clear the label (old behavior)
      setIsHovered(false);
      onDelete();
    }
    setShowDeletePathDialog(false);
  };

  const handleDuplicate = () => {
    if (onDuplicate) {
      // Close dropdown and reset state before duplicating
      setIsDropdownOpen(false);
      setIsHovered(false);
      onDuplicate();
    }
  };

  // If editing, show input field (check this BEFORE null/empty check)
  if (isEditing) {
    return (
      <div className={`inline-flex items-center ${className}`}>
        <div className="relative">
          <Input
            ref={inputRef}
            value={editValue}
            onChange={(e) => {
              setEditValue(e.target.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder="Enter label..."
            maxLength={MAX_EDGE_LABEL_LENGTH}
            className="h-7 px-2 py-1 text-xs w-28 bg-white border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          />
          {error && (
            <div className="absolute top-full mt-1 text-xs text-red-600 whitespace-nowrap z-10 bg-white px-1 rounded shadow-sm">
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // If label is null or empty, show only ellipses on hover (only if handlers exist)
  if (!label || label.trim() === '') {
    // Don't show anything if no handlers are provided (like for end nodes)
    const hasHandlers = onSave || onDelete || onDuplicate || onDeleteNode || onDeletePath;
    if (!hasHandlers) {
      return null; // No interaction for end nodes
    }

    return (
      <>
        <div
          className={`inline-flex items-center justify-center min-w-12 min-h-8 px-2 ${className}`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{ cursor: 'pointer' }}
        >
          {(isHovered || isDropdownOpen) && isEditingMode && (
            <DropdownMenu onOpenChange={setIsDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 w-6 p-0 border ${isDropdownOpen ? 'bg-grey-2 border-grey-6' : 'border-transparent hover:bg-grey-2 hover:border-grey-6'}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <EllipsisVerticalIcon className="h-3 w-3 text-grey-6" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="right" sideOffset={10} className="w-40 z-50 !rounded-xl">
                <DropdownMenuItem onClick={handleRename} className="!cursor-pointer hover:!bg-grey-2 rounded-md">
                  Rename
                </DropdownMenuItem>
                {onDuplicate && (
                  <DropdownMenuItem onClick={handleDuplicate} className="!cursor-pointer hover:!bg-grey-2 rounded-md">
                    Duplicate Path
                  </DropdownMenuItem>
                )}
                {onDeletePath && (
                  <DropdownMenuItem onClick={handleDeletePath} className="text-red-4 !cursor-pointer hover:!bg-grey-2 rounded-md">
                    Delete Path
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Delete Path Confirmation Dialog */}
        <AlertDialog open={showDeletePathDialog} onOpenChange={setShowDeletePathDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Path</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this path? This will remove all nodes in this branch. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowDeletePathDialog(false)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDeletePath}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete Path
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Default state: show label with ellipses menu
  return (
    <>
      <div
        className={`inline-flex items-center bg-grey-1 border rounded-lg h-6 ${className} border-grey-6`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className="px-2 py-0.5 text-xs font-medium">
          {label}
        </span>
        {(isHovered || isDropdownOpen) && isEditingMode && (
          <DropdownMenu onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-grey-2 border-none"
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisVerticalIcon className="h-3 w-3 text-grey-6" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="right" sideOffset={10} className="w-40 z-50 !rounded-xl">
              <DropdownMenuItem onClick={handleRename} className="!cursor-pointer hover:!bg-grey-2 rounded-md">
                Rename
              </DropdownMenuItem>
              {onDuplicate && (
                <DropdownMenuItem onClick={handleDuplicate} className="!cursor-pointer hover:!bg-grey-2 rounded-md">
                  Duplicate Path
                </DropdownMenuItem>
              )}
              {onDeletePath && (
                <DropdownMenuItem onClick={handleDeletePath} className="text-red-600 !cursor-pointer hover:!bg-red-50 rounded-md">
                  Delete Path
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Delete Path Confirmation Dialog */}
      <AlertDialog open={showDeletePathDialog} onOpenChange={setShowDeletePathDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Path</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this path? This will remove all nodes in this branch. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeletePathDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePath}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Path
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
